import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router";
import { Alert, Layout, Spin, message, Input, Select, Button } from "antd";
import dayjs from "dayjs";
import axios from "axios";

import {
  useLoadingContext,
  useRoomCacheContext,
} from "../../context/roomContext";
import { useMeetingConfig } from "../zoom/useMeetingConfig";
import { saveDevicePrefs } from "../zoom/devicePrefs";
import { usePreviewAV } from "../zoom/usePreviewAV";
import PreviewFooter from "../components/PreviewFooter";

import "../styles/waitingRoom.scss";

const { Content } = Layout;

function errToText(e: any) {
  if (!e) return "Unknown error";
  if (typeof e === "string") return e;
  if (e.reason) return String(e.reason);
  if (e.type && e.reason) return `${e.type}: ${e.reason}`;
  if (e.message) return String(e.message);

  try {
    return JSON.stringify(e);
  } catch {
    return String(e);
  }
}

export default function WaitingRoom() {
  const { cid } = useParams();
  const navigate = useNavigate();

  const [isGuestAttendee, setIsGuestAttendee] = useState(false);

  const {
    meetingConfig,
    roomCache,
  } = useRoomCacheContext();

  const {
    setShowLoading,
  } = useLoadingContext();
  const { loading, error } = useMeetingConfig(cid || "");

  const preview = usePreviewAV();
  const videoElRef = useRef<HTMLVideoElement | null>(null);
  const didAutoStartPreviewRef = useRef(false);

  const [alertType, setAlertType] = useState<"success" | "warning" | "error" | undefined>("warning");
  const [alertMsg, setAlertMsg] = useState<string>("Loading appointment…");
  const [timeVariantError, setTimeVariantError] = useState(false);
  const [consultExpired, setConsultExpired] = useState(false);

  const [attendeeName, setAttendeeName] = useState("");
  const [attendeeRelation, setAttendeeRelation] = useState("");

  useEffect(() => {
    if (preview.activeMicId) saveDevicePrefs({ micId: preview.activeMicId });
  }, [preview.activeMicId]);

  useEffect(() => {
    if (preview.activeSpeakerId) saveDevicePrefs({ speakerId: preview.activeSpeakerId });
  }, [preview.activeSpeakerId]);

  useEffect(() => {
    if (preview.activeCameraId) saveDevicePrefs({ cameraId: preview.activeCameraId });
  }, [preview.activeCameraId]);

  useEffect(() => {
    if (!roomCache) return;

    const now = dayjs();
    const timeVariant = process.env.START_DIFF ? parseInt(process.env.START_DIFF, 10) : 30;

    const isToday = dayjs(roomCache.appointmentTime).isSame(now, "day");

    if (!isToday) {
      setAlertType("error");
      setAlertMsg(
        `Appointment not today, please return on ${dayjs(roomCache.appointmentTime).format(
          "ddd DD MMM"
        )} at ${dayjs(roomCache.appointmentTime).format("hh:mm A")}`
      );
      setTimeVariantError(true);
      return;
    }

    const minsUntil = dayjs(roomCache.appointmentTime).diff(now, "minute");

    if (minsUntil > timeVariant) {
      setTimeVariantError(true);
      setAlertType("error");
      setAlertMsg(`Appointment start time is more than ${timeVariant} minutes away`);
      return;
    }

    setTimeVariantError(false);

    const minsSince = dayjs().diff(roomCache.appointmentTime, "minute");

    if (minsSince >= roomCache.linkExpireTime) {
      setConsultExpired(true);
      setAlertType("error");
      setAlertMsg("Telehealth appointment link has expired.");
      return;
    }

    setConsultExpired(false);

    if (meetingConfig?.role !== 1) {
      setAlertType(roomCache.doctorInConsult ? "success" : "warning");
      setAlertMsg(
        roomCache.doctorInConsult
          ? "Your practitioner has arrived and is waiting."
          : "Your practitioner will arrive shortly, please join to let them know you are waiting."
      );
    } else {
      setAlertType(undefined);
      setAlertMsg("");
    }
  }, [roomCache, meetingConfig]);

  useEffect(() => {
    if (consultExpired && cid) {
      navigate(`/consult-expired/${cid}`);
    }
  }, [consultExpired, cid, navigate]);

  const onToggleVideo = useCallback(async () => {
    await preview.toggleVideo(videoElRef.current);
  }, [preview]);

  const onToggleAudio = useCallback(async () => {
    await preview.toggleMute();
  }, [preview]);

  useEffect(() => {
    if (didAutoStartPreviewRef.current) return;
    if (!videoElRef.current) return;

    didAutoStartPreviewRef.current = true;

    (async () => {
      try {
        if (!preview.isStartedVideo) {
          await preview.toggleVideo(videoElRef.current);
        }

        if (!preview.isStartedAudio || preview.isMuted) {
          await preview.toggleMute();
        }

        saveDevicePrefs({
          videoOn: true,
          micMuted: false,
        });
      } catch (e) {
        console.warn("Auto-start preview AV failed", e);
      }
    })();
  }, [preview]);

  const onSelectAudio = useCallback(
    async (key: string) => {
      const [type, deviceId] = key.split("|");
      if (!type || !deviceId) return;

      if (type === "microphone") {
        await preview.switchMicrophone(deviceId);
        saveDevicePrefs({ micId: deviceId });
      }

      if (type === "speaker") {
        preview.selectSpeaker(deviceId);
        saveDevicePrefs({ speakerId: deviceId });
      }
    },
    [preview]
  );

  const onSelectCamera = useCallback(
    async (deviceId: string) => {
      await preview.switchCamera(deviceId);
      saveDevicePrefs({ cameraId: deviceId });
    },
    [preview]
  );

  const joinAndGo = useCallback(async () => {
    if (!cid || !meetingConfig || !roomCache) return;

    if (isGuestAttendee) {
      if (!attendeeName.trim()) {
        message.error("Please enter your name.");
        return;
      }

      if (!attendeeRelation.trim()) {
        message.error("Please enter your relationship to the patient.");
        return;
      }
    }

    setShowLoading(true);

    try {
      const isDoctorNow = meetingConfig.role === 1;

      if (!isDoctorNow) {
        try {
          await axios.post("/api/patientWaitingRoom", { roomCache });
        } catch (e) {
          console.warn("[ERROR] patientWaitingRoom failed", e);
        }
      } else {
        try {
          await axios.post("/api/doctorArrival", {
            roomCacheId: roomCache.roomCacheId,
          });
        } catch (e) {
          console.warn("[ERROR] doctorArrival failed", e);
        }
      }

      saveDevicePrefs({
        videoOn: preview.isStartedVideo,
        micMuted: preview.isStartedAudio ? preview.isMuted : false,
      });

      if (isGuestAttendee) {
        sessionStorage.setItem(
          `telehealth_guest_attendee_v1:${cid}`,
          JSON.stringify({
            name: attendeeName.trim(),
            relationship: attendeeRelation.trim(),
            joinedAt: Date.now(),
          })
        );

        // Only save patient-added attendees.
        // Doctor-added participants such as Translator / Referral Dr / Other are session-only.
        if (!isDoctorNow) {
          try {
            await axios.post("/api/addConsultAttendee", {
              roomCacheId: roomCache.roomCacheId,
              attendee: {
                name: attendeeName.trim(),
                relationship: attendeeRelation.trim(),
                joinedAt: new Date().toISOString(),
              },
            });
          } catch (e) {
            console.warn("[WARN] addConsultAttendee failed", e);
          }
        }
      }

      await preview.stopAll();

      navigate(`/consult/${cid}`, { replace: true });
    } catch (e) {
      console.warn("joinAndGo failed", e);
      message.error(`Join failed: ${errToText(e)}`);
      setShowLoading(false);
    }
  }, [
    cid,
    meetingConfig,
    roomCache,
    isGuestAttendee,
    attendeeName,
    attendeeRelation,
    preview,
    navigate,
    setShowLoading,
  ]);

  if (loading) {
    return (
      <div style={{ padding: 24, display: "flex", justifyContent: "center" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 16 }}>
        <Alert type="error" message={error} />
      </div>
    );
  }

  if (!meetingConfig || !roomCache) {
    return <div style={{ padding: 16 }}>Missing meeting config.</div>;
  }

  const isDoctor = meetingConfig.role === 1;

  return (
    <div className="js-preview-view">
      <div id="js-preview-view" className="container preview__root">
        <Content>
          <div className="container video-app">
            <div className="preview-video">
              <video
                ref={videoElRef}
                muted
                playsInline
                autoPlay
                className="preview-video-show"
                style={{ objectFit: "contain" }}
              />

              <div className="video-operations video-operations-preview">
                <div className="footer-container">
                  <PreviewFooter
                    disabled={false}
                    isStartedAudio={preview.isStartedAudio}
                    isMuted={preview.isMuted}
                    micList={preview.micList}
                    speakerList={preview.speakerList}
                    activeMicId={preview.activeMicId}
                    activeSpeakerId={preview.activeSpeakerId}
                    onToggleAudio={onToggleAudio}
                    onSelectAudio={onSelectAudio}
                    isStartedVideo={preview.isStartedVideo}
                    cameraList={preview.cameraList}
                    activeCameraId={preview.activeCameraId}
                    onToggleVideo={onToggleVideo}
                    onSelectCamera={onSelectCamera}
                  />
                </div>
              </div>
            </div>

            <div className="message-join">
              <div className="patient-options">
                {alertType && alertMsg !== "" && (
                  <div className="alert-container">
                    <Alert type={alertType} description={alertMsg} />
                  </div>
                )}

                <div className="patient-container">
                  <h2>
                    {isDoctor ? (
                      <>Welcome {roomCache.doctorName || meetingConfig.name}</>
                      ) : isGuestAttendee ? (
                        <>Join consult as an additional attendee</>
                      ) : (
                        <>
                          Welcome <strong>{meetingConfig.name}</strong>
                        </>
                    )}
                  </h2>

                  {isGuestAttendee && (
                    <div style={{ marginTop: 16, marginBottom: 16, width: "100%", maxWidth: 420 }}>
                      <Input
                        placeholder="Your name"
                        value={attendeeName}
                        onChange={(e) => setAttendeeName(e.target.value)}
                        style={{ marginBottom: 10 }}
                      />

                      <Select
                        placeholder={isDoctor ? "Participant role" : "Relationship to patient"}
                        value={attendeeRelation || undefined}
                        onChange={(value) => setAttendeeRelation(value)}
                        style={{ width: "100%", marginBottom: 10 }}
                        options={isDoctor
                          ? [
                              { value: "Translator", label: "Translator" },
                              { value: "Referral Dr", label: "Referral Dr" },
                              { value: "Other", label: "Other" },
                            ]
                          : [
                              { value: "Parent", label: "Parent" },
                              { value: "Guardian", label: "Guardian" },
                              { value: "Partner", label: "Partner" },
                              { value: "Carer", label: "Carer" },
                              { value: "Family member", label: "Family member" },
                              { value: "Support person", label: "Support person" },
                              { value: "Other", label: "Other" },
                            ]}
                      />
                      <Button
                        shape="round"
                        onClick={() => {
                          setIsGuestAttendee(false);
                          setAttendeeName("");
                          setAttendeeRelation("");
                        }}
                      >
                        Back to patient join
                      </Button>
                    </div>
                  )}

                  {!timeVariantError && (
                    <div className="button-container">
                      <div className="start-button" onClick={joinAndGo}>
                       {isGuestAttendee
                          ? isDoctor
                            ? "Join as Invited Participant"
                            : "Join as Attendee"
                          : isDoctor
                            ? "Start Consult"
                            : "Join Consult"}
                      </div>
                    </div>
                  )}

                  {!isGuestAttendee && !isDoctor && (
                    <div style={{ marginTop: 12 }}>
                      <Button
                          size="large"
                          shape="round"
                          onClick={() => {
                            setIsGuestAttendee(true);
                            setAttendeeName("");
                            setAttendeeRelation("");
                          }}
                        >
                          {isDoctor ? "Join as Invited Participant" : "Join as Attendee"}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Content>
      </div>
    </div>
  );
}